/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useRef } from "react";
import {
  IonPage,
  IonContent,
  IonIcon,
  IonToast,
  IonItem,
  IonLabel,
  IonToggle,
  IonItemGroup,
  IonRippleEffect,
  isPlatform,
  useIonRouter,
} from "@ionic/react";
import { chevronForward } from "ionicons/icons";
import Header from "../../components/Header";
import { Capacitor } from "@capacitor/core";
import { NativeBiometric } from "capacitor-native-biometric";

import { useStorage } from "../../contexts/StorageContext";
import useHaptics from "../../hooks/useHaptics";
import SelectionModal from "../../components/ui/popups/SelectionModal";
import {
  BiometricConfig,
  getBiometricConfig,
  updateBiometricConfig,
} from "../../routes/BiometricAuthHandler";
import { Preferences } from "@capacitor/preferences";
import { useBiometricAuth } from "../../hooks/useBiometrics";

interface BiometricItem {
  title: string;
  subtitle: string;
}

type ModalType =
  | "Authentication Timing"
  | "Timeout Duration"
  | "Maximum Failed Attempts"
  | null;

const MyBiometric: React.FC = () => {
  const platform = Capacitor.getPlatform();
  const router = useIonRouter();
  const { storage, isStorageReady } = useStorage();
  const { buttonTap, successActivity, errorFeedback } = useHaptics();

  const [showModal, setShowModal] = useState(false);
  const [currentModalType, setCurrentModalType] = useState<ModalType>(null);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastColor, setToastColor] = useState<
    "success" | "warning" | "danger" | "primary"
  >("primary");
  const { performBiometricAuth } = useBiometricAuth();
  const [isBiometricEnabled, setIsBiometricEnabled] = useState(false);

  const [config, setConfig] = useState<BiometricConfig | null>(null);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometryType, setBiometryType] = useState<string>("Biometric");
  const [biometryTypeCode, setBiometryTypeCode] = useState<number>(0);

  const [tempAuthTrigger, setTempAuthTrigger] = useState<
    "always" | "on-login" | "after-timeout"
  >("after-timeout");
  const [tempTimeoutMinutes, setTempTimeoutMinutes] = useState(15);
  const [tempMaxFailedAttempts, setTempMaxFailedAttempts] = useState(10);

  // ✅ Load both config and user preference on mount
  useEffect(() => {
    loadConfiguration();
    loadBiometricPreference(); // Load the actual user preference
    checkBiometricAvailability();
  }, []);

  const loadConfiguration = async () => {
    try {
      const currentConfig = await getBiometricConfig();
      setConfig(currentConfig);
      setTempAuthTrigger(currentConfig.authTrigger);
      setTempTimeoutMinutes(currentConfig.timeoutMinutes);
      setTempMaxFailedAttempts(currentConfig.maxFailedAttempts);
    } catch (error) {
      console.error("Error loading biometric configuration:", error);
    }
  };

  // ✅ NEW: Load the actual biometric preference from storage
  const loadBiometricPreference = async () => {
    try {
      const { value } = await Preferences.get({ key: "biometricEnabled" });
      const enabled = value === "true";
      setIsBiometricEnabled(enabled);
      console.log("Loaded biometric preference:", enabled);
    } catch (error) {
      console.error("Error loading biometric preference:", error);
      setIsBiometricEnabled(false);
    }
  };

  const checkBiometricAvailability = async () => {
    if (!Capacitor.isNativePlatform()) {
      setBiometricAvailable(false);
      return;
    }

    try {
      const result = await NativeBiometric.isAvailable();
      setBiometricAvailable(result.isAvailable);
      setBiometryTypeCode(result.biometryType);

      const typeName =
        result.biometryType === 2
          ? "Face ID"
          : result.biometryType === 1
          ? "Touch ID"
          : "Biometric";
      setBiometryType(typeName);
    } catch (error) {
      console.error("Error checking biometric availability:", error);
      setBiometricAvailable(false);
    }
  };

  const handleConfigChange = async (updates: Partial<BiometricConfig>) => {
    if (!config) return;

    const newConfig = { ...config, ...updates };
    setConfig(newConfig);

    try {
      await updateBiometricConfig(updates);
      successActivity();
    } catch (error) {
      console.error("Error saving configuration:", error);
      errorFeedback();
    }
  };

  // ✅ FIXED: Handle biometric toggle properly
  const handleBiometricToggle = async (event: CustomEvent<{ checked: boolean }>) => {
    const isEnabled = event.detail.checked;
    
    try {
      if (isEnabled) {
        // User wants to enable biometric - authenticate first
        const authSuccess = await performBiometricAuth();
        
        if (authSuccess) {
          // Authentication successful - enable biometric
          setIsBiometricEnabled(true);
          await Preferences.set({
            key: "biometricEnabled",
            value: "true",
          });
          await handleConfigChange({ enabled: true });
          console.log("Biometric enabled successfully");
        } else {
          // Authentication failed - keep disabled
          setIsBiometricEnabled(false);
          await Preferences.set({
            key: "biometricEnabled",
            value: "false",
          });
          console.log("Biometric authentication failed");
        }
      } else {
        // User wants to disable biometric - no auth needed
        setIsBiometricEnabled(false);
        await Preferences.set({
          key: "biometricEnabled",
          value: "false",
        });
        await handleConfigChange({ enabled: false });
        console.log("Biometric disabled");
      }
    } catch (error) {
      console.error("Error handling biometric toggle:", error);
      // On error, ensure it stays disabled
      setIsBiometricEnabled(false);
      await Preferences.set({
        key: "biometricEnabled",
        value: "false",
      });
      errorFeedback();
    }
  };

  const openModal = (type: ModalType) => {
    if (!config) return;

    setCurrentModalType(type);
    setTempAuthTrigger(config.authTrigger);
    setTempTimeoutMinutes(config.timeoutMinutes);
    setTempMaxFailedAttempts(config.maxFailedAttempts);

    setShowModal(true);
    buttonTap();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentModalType(null);
  };

  const handleSave = async (e: any, fieldName: string) => {
    if (!config) return;

    const value = e.detail.value;
    const updates: Partial<BiometricConfig> = {};

    switch (currentModalType) {
      case "Authentication Timing":
        updates.authTrigger = value as "always" | "on-login" | "after-timeout";
        break;
      case "Timeout Duration":
        updates.timeoutMinutes = parseInt(value);
        break;
      case "Maximum Failed Attempts":
        updates.maxFailedAttempts = parseInt(value);
        break;
    }

    await handleConfigChange(updates);
    handleCloseModal();
  };

  const handleBack = () => {
    router.goBack();
  };

  const getAuthTriggerLabel = (trigger: string) => {
    switch (trigger) {
      case "always":
        return "Always (Every time)";
      case "after-timeout":
        return "After Timeout";
      default:
        return "Unknown";
    }
  };

  const getTimeoutLabel = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
    } else {
      const hours = minutes / 60;
      return `${hours} hour${hours !== 1 ? "s" : ""}`;
    }
  };

  const getMaxAttemptsLabel = (attempts: number) => {
    if (attempts === -1) {
      return "Unlimited";
    }
    return `${attempts} attempt${attempts !== 1 ? "s" : ""}`;
  };

  const renderBiometricItem = (
    title: string,
    subtitle: string,
    onClick: () => void,
    disabled = false
  ) => (
    <div
      className={`flex justify-between items-center p-4 border-b border-gray-200 last:border-b-0 ${
        disabled ? "opacity-50" : "cursor-pointer"
      }`}
      onClick={disabled ? undefined : onClick}
    >
      <div>
        <h3 className="text-lg font-normal text-black mb-1">
          {title.length > 20 ? title.slice(0, 20) + "..." : title}
        </h3>
        <p className="text-gray-500">{subtitle}</p>
      </div>
      <div className="flex items-center">
        <IonIcon
          icon={chevronForward}
          className={disabled ? "text-gray-300" : "text-gray-400"}
        />
      </div>
    </div>
  );

  const renderBiometricSection = (
    title: string,
    items: BiometricItem[],
    onItemClick: (index: number) => void,
    disabledItems: boolean[] = []
  ) => (
    <>
      <div className="section-header">
        <div>
          <div className="settings-title !ms-4">{title}</div>
        </div>
      </div>
      <div className="bg-white rounded-xl overflow-hidden shadow-[0_16px_64px_rgba(0,0,0,0.1)] mb-4">
        {items.map((item, index) => (
          <div key={index}>
            {renderBiometricItem(
              item.title,
              item.subtitle,
              () => onItemClick(index),
              disabledItems[index]
            )}
          </div>
        ))}
      </div>
    </>
  );

  const getModalOptions = () => {
    if (!config) return [];

    switch (currentModalType) {
      case "Authentication Timing":
        return [
          {
            id: "always",
            value: "always",
            label: "Always (Every time)",
            selected: config.authTrigger === "always",
          },
          {
            id: "after-timeout",
            value: "after-timeout",
            label: "After Timeout",
            selected: config.authTrigger === "after-timeout",
          },
        ];

      case "Timeout Duration":
        return [5, 10, 15, 30, 60, 120].map((minutes) => ({
          id: minutes.toString(),
          value: minutes.toString(),
          label: getTimeoutLabel(minutes),
          selected: config.timeoutMinutes === minutes,
        }));

      case "Maximum Failed Attempts":
        return [3, 5, 10, 15, 20, -1].map((attempts) => ({
          id: attempts.toString(),
          value: attempts.toString(),
          label: getMaxAttemptsLabel(attempts),
          selected: config.maxFailedAttempts === attempts,
        }));

      default:
        return [];
    }
  };

  const getModalTitle = () => {
    switch (currentModalType) {
      case "Authentication Timing":
        return "Authentication Timing";
      case "Timeout Duration":
        return "Timeout Duration";
      case "Maximum Failed Attempts":
        return "Maximum Failed Attempts";
      default:
        return "";
    }
  };

  const getModalSubtitle = () => {
    switch (currentModalType) {
      case "Authentication Timing":
        return "Choose when to require authentication";
      case "Timeout Duration":
        return "Require authentication after inactivity";
      case "Maximum Failed Attempts":
        return "Force logout after failed attempts";
      default:
        return "";
    }
  };

  if (!config) {
    return null;
  }

  const authenticationSettings: BiometricItem[] = [
    {
      title: "Authentication Timing",
      subtitle: getAuthTriggerLabel(config.authTrigger),
    },
  ];

  if (config.authTrigger === "after-timeout") {
    authenticationSettings.push({
      title: "Timeout Duration",
      subtitle: getTimeoutLabel(config.timeoutMinutes),
    });
  }

  authenticationSettings.push({
    title: "Maximum Failed Attempts",
    subtitle: getMaxAttemptsLabel(config.maxFailedAttempts),
  });

  return (
    <>
      <IonPage>
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="top"
          color={toastColor}
        />

        <Header
          showGreeting={false}
          showPageTitle={true}
          showUserIcon={false}
          pageTitle={`${biometryType} Settings`}
          showBackButton={true}
          onBackButtonClick={handleBack}
        />

        <IonContent
          className="ion-padding"
          style={{ "--background": "#F7F5FA" }}
        >
          <div className="flex flex-col">
            {!biometricAvailable && platform !== "web" && (
              <div
                className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded-r-lg"
                style={{
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                }}
              >
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-yellow-400 text-xl">
                      &#9888;&#65039;
                    </span>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Biometric Authentication Not Available
                    </h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      Your device doesn&#39;t support biometric authentication
                      or it hasn&#39;t been set up. Please enable{" "}
                      {biometryType.toLowerCase()} in your device settings.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {platform === "web" && (
              <div
                className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 rounded-r-lg"
                style={{
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                }}
              >
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-blue-400 text-xl">
                      &#8505;&#65039;
                    </span>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Mobile App Required
                    </h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Biometric authentication is only available on our mobile
                      apps. Download from the App Store or Google Play.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Biometric Toggle Section */}
            <div className="section-header">
              <div>
                <div className="settings-title !mt-0 !ms-4">
                  BIOMETRIC AUTHENTICATION
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl overflow-hidden shadow-[0_16px_64px_rgba(0,0,0,0.1)] mb-4">
              <IonItemGroup>
                <IonItem
                  className="settings-item"
                  onClick={(e) => e.preventDefault()}
                  lines="none"
                  disabled={!biometricAvailable || platform === "web"}
                >
                  <IonLabel>
                    <h2 className="text-lg font-normal text-black">
                      Enable {biometryType}
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                      Secure your app with {biometryType.toLowerCase()}{" "}
                      authentication
                    </p>
                  </IonLabel>
                  {/* ✅ FIXED: Use isBiometricEnabled instead of config.enabled */}
                  <IonToggle
                    slot="end"
                    onIonChange={handleBiometricToggle}
                    checked={isBiometricEnabled}
                    disabled={!biometricAvailable || platform === "web"}
                  />
                  <IonRippleEffect />
                </IonItem>
              </IonItemGroup>
            </div>

            {/* Authentication Settings Section */}
            {renderBiometricSection(
              "AUTHENTICATION SETTINGS",
              authenticationSettings,
              (index) => {
                if (!isBiometricEnabled) return; // ✅ FIXED: Use isBiometricEnabled

                if (index === 0) {
                  openModal("Authentication Timing");
                } else if (
                  index === 1 &&
                  config.authTrigger === "after-timeout"
                ) {
                  openModal("Timeout Duration");
                } else if (
                  (index === 1 && config.authTrigger !== "after-timeout") ||
                  index === 2
                ) {
                  openModal("Maximum Failed Attempts");
                }
              },
              authenticationSettings.map(() => !isBiometricEnabled) // ✅ FIXED: Use isBiometricEnabled
            )}

            {/* Info Section */}
            <div
              className="bg-white rounded-xl p-4 shadow-[0_16px_64px_rgba(0,0,0,0.1)] mb-4"
              style={{ paddingBottom: "80px" }}
            >
              <h3 className="text-lg font-semibold mb-3">
                How {biometryType} Works
              </h3>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex">
                  <span className="mr-2">&#8226;</span>
                  <span>
                    <strong>Always:</strong> Requires biometric authentication
                    every time you access the app
                  </span>
                </li>
                <li className="flex">
                  <span className="mr-2">&#8226;</span>
                  <span>
                    <strong>After Timeout:</strong> Requires authentication
                    after the specified period of inactivity
                  </span>
                </li>
              </ul>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  Your biometric data never leaves your device and is not shared
                  with our servers. Authentication is handled entirely by your
                  device&#39;s secure biometric system.
                </p>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>

      {showModal && (
        <SelectionModal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={getModalTitle()}
          subtitle={getModalSubtitle()}
          buttonText="Save"
          options={getModalOptions()}
          onSave={handleSave}
          multiSelect={false}
          fieldName={currentModalType || ""}
          allowCustomOptions={false}
          showSearch={false}
          selectedOnTop={false}
        />
      )}
    </>
  );
};

export default MyBiometric;